<template>
  <div class="live-transcript-overlay" v-show="isVisible">
    <div
      class="live-transcript-container futuristic-modern"
      :style="{
        background: backgroundColorLocal,
        width: isMobile ? 'auto' : containerWidth,
        height: containerHeight
      }"
      v-if="displayText"
    >
      <!-- Holographic background effect -->
      <div class="holographic-bg"></div>

      <!-- Dynamic gradient border -->
      <div class="animated-border"></div>

      <!-- Floating particles -->
      <div class="particle-system">
        <div v-for="n in 8" :key="`particle-${n}`" class="particle"></div>
      </div>

      <!-- Neural network lines -->
      <div class="neural-network">
        <svg class="network-svg" viewBox="0 0 400 200">
          <g class="network-lines">
            <line v-for="(line, index) in networkLines"
                  :key="`line-${index}`"
                  :x1="line.x1" :y1="line.y1"
                  :x2="line.x2" :y2="line.y2"
                  class="network-line"
                  :style="{ animationDelay: `${index * 0.2}s` }" />
          </g>
          <g class="network-nodes">
            <circle v-for="(node, index) in networkNodes"
                    :key="`node-${index}`"
                    :cx="node.x" :cy="node.y"
                    :r="node.r"
                    class="network-node"
                    :style="{ animationDelay: `${index * 0.3}s` }" />
          </g>
        </svg>
      </div>

      <!-- Speaker section with AI-inspired design -->
      <div class="speaker-section">
        <!-- Futuristic speaker badge -->
        <div class="speaker-name-container" v-if="showSpeaker">
          <div class="speaker-badge-futuristic">
            <div class="status-ring" :class="{ 'active': !isPaused }">
              <div class="status-core"></div>
              <div class="status-pulse" :class="{ 'active': !isPaused }"></div>
            </div>
            <span class="speaker-name-text">{{ currentSpeaker }}</span>
            <div class="badge-glow"></div>
          </div>
        </div>

        <!-- Avatar with holographic frame -->
        <div class="transcript-speaker-avatar" :class="{ 'speaking': !isPaused }">
          <!-- Holographic frame layers -->
          <div class="holo-frame-outer"></div>
          <div class="holo-frame-inner"></div>

          <!-- Avatar content -->
          <div class="avatar-content">
            <img
              v-if="speakerImage"
              :src="speakerImage"
              :alt="currentSpeaker"
              class="avatar-image"
              @error="handleImageError"
            />
            <div v-else class="avatar-fallback">
              {{ getSpeakerInitials() }}
            </div>
          </div>

          <!-- Quantum-style audio visualizer -->
          <div class="quantum-visualizer" :class="{ 'active': !isPaused }">
            <div class="quantum-ring quantum-ring-1"></div>
            <div class="quantum-ring quantum-ring-2"></div>
            <div class="quantum-ring quantum-ring-3"></div>
            <div class="quantum-center"></div>
          </div>

          <!-- Energy field -->
          <div class="energy-field" :class="{ 'active': !isPaused }"></div>
        </div>
      </div>

      <!-- Content section with glassmorphism -->
      <div class="transcript-content">
        <div class="transcript-text-container">
          <!-- Text background effects -->
          <div class="text-bg-effect"></div>
          <div class="scan-line" :class="{ 'active': !isPaused }"></div>

          <div
            class="transcript-text"
            :style="computedFontStyles"
            ref="transcriptTextElement"
          >
            <span v-html="typewriterText"></span>
            <span class="cursor-modern" :class="{ 'blink': cursorBlinking }" :style="computedCursorStyles">
              <span class="cursor-core">|</span>
              <span class="cursor-glow"></span>
            </span>
          </div>

          <!-- Progress indicator -->
          <div class="typing-progress">
            <div class="progress-bar" :style="{ width: progressPercentage + '%' }"></div>
          </div>
        </div>
      </div>

      <!-- Corner accents -->
      <div class="corner-accent corner-top-left"></div>
      <div class="corner-accent corner-top-right"></div>
      <div class="corner-accent corner-bottom-left"></div>
      <div class="corner-accent corner-bottom-right"></div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'LiveTranscript',
  props: {
    text: { type: String, default: '' },
    speaker: { type: String, default: '' },
    speakerImage: { type: String, default: '' },
    visible: { type: Boolean, default: true },
    showSpeaker: { type: Boolean, default: true },
    maxLength: { type: Number, default: 1200 },
    messageId: { required: true },
    typingSpeed: { type: Number, default: 40 },
    restartOnTextChange: { type: Boolean, default: true },
    voiceSpeed: { type: Number, default: 1.2 },
    backgroundColor: { type: String, default: 'rgba(5, 10, 48, 0.8)' },
    fontFamily: { type: String, default: '"Inter", "SF Pro Display", system-ui, sans-serif' },
    fontSize: { type: [String, Number], default: 18 },
    fontWeight: { type: [String, Number], default: 500 },
    lineHeight: { type: [String, Number], default: 1.6 },
    letterSpacing: { type: [String, Number], default: 0.5 },
    textColor: { type: String, default: '#ffffff' },
    textShadow: { type: String, default: '0 2px 8px rgba(0, 0, 0, 0.5)' }
  },
  data() {
    return {
      backgroundColorLocal: this.backgroundColor,
      isVisible: this.visible,
      currentSpeaker: this.speaker,
      fullText: this.text,
      displayText: '',
      typewriterText: '',
      typingIndex: 0,
      typingTimer: null,
      cursorBlinking: false,
      cursorTimer: null,
      isPaused: false,
      pauseTimer: null,
      containerWidth: 'auto',
      containerHeight: 'auto',
      textWidth: 0,
      textHeight: 0,
      minContainerWidth: 350,
      maxContainerWidthDesktop: '75%',
      maxContainerWidthMobile: '95%',
      maxWidthInPixels: 900,
      minContainerHeight: 140,
      charWidthEstimate: 13,
      lineHeightEstimate: 28,
      horizontalPadding: 40,
      verticalPadding: 28,
      avatarWidth: 120,
      avatarSpacing: 20,
      isMobile: false,
      pausePunctuation: {
        '.': 15, '!': 15, '?': 15, ',': 8, ';': 9, ':': 10,
        '-': 5, '—': 8, '(': 4, ')': 5, '\n': 8, '\r': 8,
        '…': 18, '"': 4, "'": 3
      },
      regexPatterns: {
        citation: /\*\*c\d+\*\*/g,
        abbreviation: /(?:\b[A-Z]\.)+(?:[A-Z]\b|[A-Z]\.)/g
      },
      // Neural network data
      networkNodes: [
        { x: 50, y: 50, r: 3 }, { x: 150, y: 30, r: 2 }, { x: 250, y: 70, r: 3 },
        { x: 350, y: 40, r: 2 }, { x: 80, y: 120, r: 2 }, { x: 180, y: 140, r: 3 },
        { x: 280, y: 130, r: 2 }, { x: 320, y: 160, r: 3 }
      ],
      networkLines: [
        { x1: 50, y1: 50, x2: 150, y2: 30 }, { x1: 150, y1: 30, x2: 250, y2: 70 },
        { x1: 250, y1: 70, x2: 350, y2: 40 }, { x1: 50, y1: 50, x2: 80, y2: 120 },
        { x1: 150, y1: 30, x2: 180, y2: 140 }, { x1: 250, y1: 70, x2: 280, y2: 130 },
        { x1: 350, y1: 40, x2: 320, y2: 160 }, { x1: 80, y1: 120, x2: 180, y2: 140 },
        { x1: 180, y1: 140, x2: 280, y2: 130 }, { x1: 280, y1: 130, x2: 320, y2: 160 }
      ]
    }
  },
  computed: {
    computedFontStyles() {
      let fontSize = typeof this.fontSize === 'number' ? `${this.fontSize}px` : this.fontSize;
      if (typeof this.fontSize === 'string' && !this.fontSize.includes('px')) {
        const numValue = parseFloat(this.fontSize);
        fontSize = isNaN(numValue) ? this.fontSize : `${numValue}px`;
      }

      let letterSpacing = typeof this.letterSpacing === 'number' ? `${this.letterSpacing}px` : this.letterSpacing;
      const lineHeight = typeof this.lineHeight === 'number' ? this.lineHeight.toString() : this.lineHeight;
      const fontWeight = typeof this.fontWeight === 'number' ? this.fontWeight.toString() : this.fontWeight;

      return {
        '--font-family': this.fontFamily,
        '--font-size': fontSize,
        '--font-weight': fontWeight,
        '--line-height': lineHeight,
        '--letter-spacing': letterSpacing,
        '--text-color': this.textColor,
        '--text-shadow': this.textShadow,
        fontFamily: this.fontFamily,
        fontSize: fontSize,
        fontWeight: fontWeight,
        lineHeight: lineHeight,
        letterSpacing: letterSpacing,
        color: this.textColor,
        textShadow: this.textShadow,
        minHeight: '1.6em',
        wordWrap: 'break-word',
        width: '100%'
      }
    },
    computedCursorStyles() {
      const fontSize = typeof this.fontSize === 'number' ? `${this.fontSize}px` : this.fontSize;
      return {
        fontFamily: this.fontFamily,
        fontSize: fontSize,
        fontWeight: this.fontWeight,
        color: this.textColor
      }
    },
    dynamicCharWidth() {
      const baseFontSize = 18;
      const currentFontSize = typeof this.fontSize === 'number' ? this.fontSize : parseInt(this.fontSize);
      return this.charWidthEstimate * (currentFontSize / baseFontSize);
    },
    dynamicLineHeight() {
      const baseFontSize = 18;
      const currentFontSize = typeof this.fontSize === 'number' ? this.fontSize : parseInt(this.fontSize);
      const lineHeightMultiplier = typeof this.lineHeight === 'number' ? this.lineHeight : parseFloat(this.lineHeight);
      return currentFontSize * lineHeightMultiplier;
    },
    progressPercentage() {
      if (!this.displayText) return 0;
      return Math.min((this.typingIndex / this.displayText.length) * 100, 100);
    }
  },
  watch: {
    text: {
      immediate: true,
      handler(newText) {
        this.fullText = newText;
        this.processText();
        this.calculateContainerSize();
        if (this.restartOnTextChange && this.visible) {
          this.resetTypewriter();
        }
      }
    },
    speaker(newSpeaker) { this.currentSpeaker = newSpeaker; },
    visible(newVisible) {
      const oldVisible = this.isVisible;
      this.isVisible = newVisible;
      if (!newVisible) {
        this.stopTypewriter();
        this.clearPauseTimer();
        this.cursorBlinking = false;
        this.isPaused = true;
      } else if (this.displayText) {
        this.calculateContainerSize();
        if (!oldVisible && newVisible) {
          this.resetTypewriter();
        } else {
          setTimeout(() => { this.startTypewriter(); }, 200);
        }
      }
    },
    voiceSpeed() {
      if (this.typingIndex < this.displayText.length) {
        this.resetTypewriter();
      }
    },
    backgroundColor(newColor) { this.backgroundColorLocal = newColor; },
    fontSize() { this.calculateContainerSize(); this.forceUpdateFontStyles(); },
    fontFamily() { this.calculateContainerSize(); this.forceUpdateFontStyles(); },
    lineHeight() { this.calculateContainerSize(); this.forceUpdateFontStyles(); },
    fontWeight() { this.forceUpdateFontStyles(); },
    letterSpacing() { this.forceUpdateFontStyles(); },
    textColor() { this.forceUpdateFontStyles(); }
  },
  mounted() {
    this.detectMobileDevice();
    window.addEventListener('resize', this.handleResize);
    this.calculateContainerSize();
    this.startCursorBlink();
    this.forceUpdateFontStyles();
  },
  beforeDestroy() {
    this.stopTypewriter();
    this.stopCursorBlink();
    this.clearPauseTimer();
    window.removeEventListener('resize', this.handleResize);
  },
  methods: {
    detectMobileDevice() { this.isMobile = window.innerWidth <= 768; },
    handleResize() {
      const wasMobile = this.isMobile;
      this.detectMobileDevice();
      if (wasMobile !== this.isMobile) {
        this.calculateContainerSize();
      }
    },
    calculateContainerSize() {
      if (!this.displayText) {
        this.containerWidth = 'auto';
        this.containerHeight = 'auto';
        return;
      }

      const avatarSize = this.isMobile ? 80 : this.avatarWidth;
      const avatarGap = this.isMobile ? 16 : this.avatarSpacing;
      const charWidth = this.isMobile ? this.dynamicCharWidth * 0.75 : this.dynamicCharWidth;
      const lineHeight = this.isMobile ? this.dynamicLineHeight * 0.95 : this.dynamicLineHeight;
      const maxWidthPercent = this.isMobile ? 0.95 : 0.8;

      let contentWidth = Math.min(
        Math.max(this.displayText.length * charWidth, this.minContainerWidth),
        window.innerWidth * maxWidthPercent,
        this.maxWidthInPixels
      );

      const availableTextWidth = contentWidth - avatarSize - avatarGap - this.horizontalPadding;
      const charsPerLine = Math.max(12, Math.floor(availableTextWidth / charWidth));
      const estimatedLines = Math.max(3, Math.ceil(this.displayText.length / charsPerLine));
      const textHeight = estimatedLines * lineHeight;
      const minContentHeight = Math.max(this.minContainerHeight, avatarSize + 30);
      const contentHeight = Math.max(textHeight + this.verticalPadding, minContentHeight);
      const speakerNameHeight = this.showSpeaker ? (this.isMobile ? 24 : 32) : 0;

      this.containerWidth = `${contentWidth}px`;
      this.containerHeight = `${Math.min(contentHeight + speakerNameHeight, window.innerHeight * 0.65)}px`;
    },
    handleImageError(event) {
      event.target.style.display = 'none';
      const container = event.target.parentNode;
      const fallback = document.createElement('div');
      fallback.className = 'avatar-fallback';
      fallback.textContent = this.getSpeakerInitials();
      container.appendChild(fallback);
    },
    getSpeakerInitials() {
      if (!this.currentSpeaker) return '?';
      return this.currentSpeaker.split(' ').map(word => word.charAt(0)).join('').toUpperCase().substring(0, 2);
    },
    processText() {
      if (!this.fullText) {
        this.displayText = '';
        return;
      }
      let cleanText = this.fullText.replace(/<[^>]*>/g, '');
      cleanText = cleanText.replace(/\[\d+\]/g, '');
      cleanText = cleanText.replace(/\s+/g, ' ').trim();
      if (cleanText.length > this.maxLength) {
        cleanText = cleanText.substring(0, this.maxLength) + '...';
      }
      this.displayText = cleanText;
    },
    startTypewriter() {
      this.stopTypewriter();
      this.clearPauseTimer();
      this.isPaused = false;
      if (this.typingIndex >= this.displayText.length) {
        this.cursorBlinking = true;
        return;
      }
      this.typeNextCharacter();
    },
    typeNextCharacter() {
      if (this.typingIndex >= this.displayText.length) {
        this.cursorBlinking = true;
        this.isPaused = true;
        return;
      }

      this.typewriterText += this.displayText.charAt(this.typingIndex);
      let shouldPause = false;
      let pauseMultiplier = 0;

      const currentChar = this.displayText.charAt(this.typingIndex);
      const nextChar = this.typingIndex + 1 < this.displayText.length ? this.displayText.charAt(this.typingIndex + 1) : '';
      const prevChar = this.typingIndex > 0 ? this.displayText.charAt(this.typingIndex - 1) : '';

      if (this.pausePunctuation[currentChar]) {
        const isAbbreviation = /^[A-Z]\.([A-Z]|[a-z])/.test(prevChar + currentChar + nextChar);
        if (currentChar === '.' && isAbbreviation) {
          shouldPause = false;
        } else {
          shouldPause = true;
          pauseMultiplier = this.pausePunctuation[currentChar];
        }
      }

      this.typingIndex++;
      this.cursorBlinking = false;

      if (shouldPause && pauseMultiplier > 0) {
        this.addNaturalPause(pauseMultiplier);
      } else {
        this.scheduleNextCharacter();
      }
    },
    addNaturalPause(multiplier) {
      this.isPaused = true;
      const basePause = this.typingSpeed * multiplier;
      const adjustedPause = basePause / this.voiceSpeed;
      this.pauseTimer = setTimeout(() => {
        this.isPaused = false;
        this.scheduleNextCharacter();
      }, adjustedPause);
    },
    scheduleNextCharacter() {
      const delay = this.getTypingDelay();
      this.typingTimer = setTimeout(() => {
        this.typeNextCharacter();
      }, delay);
    },
    getTypingDelay() {
      let delay = this.typingSpeed / this.voiceSpeed;
      const randomFactor = 0.9 + (Math.random() * 0.2);
      delay *= randomFactor;
      return Math.max(10, Math.floor(delay));
    },
    stopTypewriter() {
      if (this.typingTimer) {
        clearTimeout(this.typingTimer);
        this.typingTimer = null;
      }
    },
    clearPauseTimer() {
      if (this.pauseTimer) {
        clearTimeout(this.pauseTimer);
        this.pauseTimer = null;
      }
    },
    resetTypewriter() {
      this.stopTypewriter();
      this.clearPauseTimer();
      this.typingIndex = 0;
      this.typewriterText = '';
      this.isPaused = false;
      this.calculateContainerSize();
      this.startTypewriter();
    },
    startCursorBlink() {
      this.cursorTimer = setInterval(() => {
        if (this.typingIndex >= this.displayText.length) {
          this.cursorBlinking = !this.cursorBlinking;
        }
      }, 600);
    },
    stopCursorBlink() {
      if (this.cursorTimer) {
        clearInterval(this.cursorTimer);
        this.cursorTimer = null;
      }
    },
    forceUpdateFontStyles() {
      this.$nextTick(() => {
        const textElement = this.$refs.transcriptTextElement;
        if (textElement) {
          let fontSize = typeof this.fontSize === 'number' ? `${this.fontSize}px` : this.fontSize;
          if (typeof this.fontSize === 'string' && !this.fontSize.includes('px')) {
            const numValue = parseFloat(this.fontSize);
            fontSize = isNaN(numValue) ? this.fontSize : `${numValue}px`;
          }

          let letterSpacing = typeof this.letterSpacing === 'number' ? `${this.letterSpacing}px` : this.letterSpacing;
          const lineHeight = typeof this.lineHeight === 'number' ? this.lineHeight.toString() : this.lineHeight;
          const fontWeight = typeof this.fontWeight === 'number' ? this.fontWeight.toString() : this.fontWeight;

          textElement.style.setProperty('--font-size', fontSize);
          textElement.style.setProperty('--font-family', this.fontFamily);
          textElement.style.setProperty('--font-weight', fontWeight);
          textElement.style.setProperty('--line-height', lineHeight);
          textElement.style.setProperty('--letter-spacing', letterSpacing);
          textElement.style.setProperty('--text-color', this.textColor);
          textElement.style.setProperty('--text-shadow', this.textShadow);

          textElement.style.fontFamily = this.fontFamily;
          textElement.style.fontSize = fontSize;
          textElement.style.fontWeight = fontWeight;
          textElement.style.lineHeight = lineHeight;
          textElement.style.letterSpacing = letterSpacing;
          textElement.style.color = this.textColor;
          textElement.style.textShadow = this.textShadow;

          textElement.offsetHeight;
        }
      });
    }
  }
}
</script>

<style>
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap');

/* Root variables for consistent theming */
:root {
  --primary-glow: #00f5ff;
  --secondary-glow: #ff0080;
  --accent-glow: #8000ff;
  --neural-blue: #0099ff;
  --quantum-purple: #6600ff;
  --energy-cyan: #00ffcc;
  --hologram-pink: #ff3366;
}

.live-transcript-overlay {
  position: absolute;
  bottom: 80px;
  left: 0;
  right: 0;
  z-index: 10;
  display: flex;
  justify-content: center;
  pointer-events: none;
  perspective: 1000px;
}

.live-transcript-container {
  position: relative;
  border-radius: 24px;
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 20px;
  box-shadow:
    0 20px 60px rgba(0, 245, 255, 0.15),
    0 8px 32px rgba(0, 0, 0, 0.4),
    inset 0 1px 0 rgba(255, 255, 255, 0.1);
  transition: all 0.6s cubic-bezier(0.23, 1, 0.32, 1);
  backdrop-filter: blur(20px) saturate(180%);
  overflow: hidden;
  min-height: 140px;
  border: 1px solid rgba(0, 245, 255, 0.2);
  animation: containerEntrance 0.8s cubic-bezier(0.23, 1, 0.32, 1);
  transform-style: preserve-3d;
}

.futuristic-modern {
  background: linear-gradient(145deg,
    rgba(5, 10, 48, 0.85) 0%,
    rgba(15, 25, 70, 0.9) 30%,
    rgba(8, 15, 55, 0.88) 70%,
    rgba(2, 8, 40, 0.92) 100%);
  position: relative;
}

@keyframes containerEntrance {
  0% {
    transform: translateY(40px) rotateX(10deg);
    opacity: 0;
    filter: blur(10px);
  }
  50% {
    transform: translateY(-5px) rotateX(-2deg);
    opacity: 0.8;
  }
  100% {
    transform: translateY(0) rotateX(0deg);
    opacity: 1;
    filter: blur(0px);
  }
}

/* Holographic background effect */
.holographic-bg {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(45deg,
    transparent 30%,
    rgba(0, 245, 255, 0.03) 50%,
    transparent 70%);
  animation: holographicShift 4s ease-in-out infinite;
  pointer-events: none;
  z-index: 1;
}

@keyframes holographicShift {
  0%, 100% { transform: translateX(-100%); }
  50% { transform: translateX(100%); }
}

/* Animated border */
.animated-border {
  position: absolute;
  top: -2px;
  left: -2px;
  width: calc(100% + 4px);
  height: calc(100% + 4px);
  border-radius: 26px;
  background: linear-gradient(45deg,
    var(--primary-glow),
    var(--secondary-glow),
    var(--accent-glow),
    var(--primary-glow));
  background-size: 300% 300%;
  animation: borderFlow 3s ease-in-out infinite;
  z-index: -1;
  opacity: 0.6;
}

@keyframes borderFlow {
  0%, 100% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
}

/* Particle system */
.particle-system {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;
  pointer-events: none;
  z-index: 2;
}

.particle {
  position: absolute;
  width: 2px;
  height: 2px;
  background: var(--primary-glow);
  border-radius: 50%;
  animation: particleFloat 8s linear infinite;
  box-shadow: 0 0 6px var(--primary-glow);
}

.particle:nth-child(odd) {
  background: var(--secondary-glow);
  box-shadow: 0 0 6px var(--secondary-glow);
  animation-duration: 12s;
}

.particle:nth-child(1) { left: 10%; animation-delay: 0s; }
.particle:nth-child(2) { left: 25%; animation-delay: -2s; }
.particle:nth-child(3) { left: 40%; animation-delay: -4s; }
.particle:nth-child(4) { left: 55%; animation-delay: -6s; }
.particle:nth-child(5) { left: 70%; animation-delay: -1s; }
.particle:nth-child(6) { left: 85%; animation-delay: -3s; }
.particle:nth-child(7) { left: 95%; animation-delay: -5s; }
.particle:nth-child(8) { left: 15%; animation-delay: -7s; }

@keyframes particleFloat {
  0% {
    transform: translateY(100vh) scale(0);
    opacity: 0;
  }
  10% {
    opacity: 1;
    transform: scale(1);
  }
  90% {
    opacity: 1;
  }
  100% {
    transform: translateY(-10vh) scale(0);
    opacity: 0;
  }
}

/* Neural network visualization */
.neural-network {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  opacity: 0.3;
  pointer-events: none;
  z-index: 1;
}

.network-svg {
  width: 100%;
  height: 100%;
}

.network-line {
  stroke: var(--neural-blue);
  stroke-width: 1;
  opacity: 0;
  animation: networkPulse 3s ease-in-out infinite;
}

.network-node {
  fill: var(--primary-glow);
  opacity: 0;
  animation: nodePulse 2s ease-in-out infinite;
}

@keyframes networkPulse {
  0%, 100% { opacity: 0; stroke-width: 1; }
  50% { opacity: 0.6; stroke-width: 2; }
}

@keyframes nodePulse {
  0%, 100% { opacity: 0.4; transform: scale(1); }
  50% { opacity: 1; transform: scale(1.5); }
}

/* Speaker section */
.speaker-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  z-index: 10;
}

.speaker-name-container {
  margin-bottom: 8px;
  width: 100%;
}

.speaker-badge-futuristic {
  position: relative;
  display: flex;
  align-items: center;
  gap: 12px;
  background: linear-gradient(135deg,
    rgba(0, 245, 255, 0.15) 0%,
    rgba(128, 0, 255, 0.15) 100%);
  border-radius: 25px;
  padding: 8px 18px;
  font-size: 14px;
  font-weight: 600;
  color: white;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
  box-shadow:
    0 4px 20px rgba(0, 245, 255, 0.2),
    inset 0 1px 0 rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(0, 245, 255, 0.3);
  backdrop-filter: blur(10px);
}

.badge-glow {
  position: absolute;
  top: -2px;
  left: -2px;
  width: calc(100% + 4px);
  height: calc(100% + 4px);
  border-radius: 27px;
  background: linear-gradient(45deg, var(--primary-glow), var(--accent-glow));
  opacity: 0;
  z-index: -1;
  animation: badgeGlow 2s ease-in-out infinite;
}

@keyframes badgeGlow {
  0%, 100% { opacity: 0; transform: scale(1); }
  50% { opacity: 0.3; transform: scale(1.05); }
}

.status-ring {
  position: relative;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.1);
  border: 2px solid rgba(255, 255, 255, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
}

.status-ring.active {
  border-color: var(--primary-glow);
  box-shadow:
    0 0 10px var(--primary-glow),
    inset 0 0 10px rgba(0, 245, 255, 0.2);
}

.status-core {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #888;
  transition: all 0.3s ease;
}

.status-ring.active .status-core {
  background: var(--primary-glow);
  box-shadow: 0 0 8px var(--primary-glow);
}

.status-pulse {
  position: absolute;
  top: -4px;
  left: -4px;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  border: 2px solid var(--primary-glow);
  opacity: 0;
  animation: none;
}

.status-pulse.active {
  animation: statusPulse 2s ease-out infinite;
}

@keyframes statusPulse {
  0% { transform: scale(0.8); opacity: 1; }
  100% { transform: scale(2); opacity: 0; }
}

.speaker-name-text {
  font-family: 'Inter', sans-serif;
  font-weight: 600;
  letter-spacing: 0.5px;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);
}

/* Avatar with holographic frame */
.transcript-speaker-avatar {
  position: relative;
  width: 120px;
  height: 120px;
  border-radius: 50%;
  overflow: visible;
  transition: all 0.4s cubic-bezier(0.23, 1, 0.32, 1);
  z-index: 10;
}

.transcript-speaker-avatar.speaking {
  transform: scale(1.08) translateY(-2px);
}

.holo-frame-outer, .holo-frame-inner {
  position: absolute;
  border-radius: 50%;
  border: 2px solid;
  animation: holoRotate 8s linear infinite;
}

.holo-frame-outer {
  top: -8px;
  left: -8px;
  width: calc(100% + 16px);
  height: calc(100% + 16px);
  border-color: var(--primary-glow);
  opacity: 0.6;
  box-shadow: 0 0 20px var(--primary-glow);
}

.holo-frame-inner {
  top: -4px;
  left: -4px;
  width: calc(100% + 8px);
  height: calc(100% + 8px);
  border-color: var(--secondary-glow);
  opacity: 0.4;
  animation-direction: reverse;
  animation-duration: 6s;
  box-shadow: 0 0 15px var(--secondary-glow);
}

@keyframes holoRotate {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.avatar-content {
  position: relative;
  width: 100%;
  height: 100%;
  border-radius: 50%;
  overflow: hidden;
  border: 3px solid rgba(255, 255, 255, 0.2);
  box-shadow:
    0 8px 32px rgba(0, 0, 0, 0.4),
    inset 0 2px 0 rgba(255, 255, 255, 0.1);
  z-index: 5;
}

.avatar-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  filter: brightness(1.1) contrast(1.1) saturate(1.2);
}

.avatar-fallback {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg,
    var(--neural-blue) 0%,
    var(--quantum-purple) 50%,
    var(--primary-glow) 100%);
  color: white;
  font-weight: 700;
  font-size: 32px;
  letter-spacing: 2px;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.5);
  font-family: 'Inter', sans-serif;
}

/* Quantum visualizer */
.quantum-visualizer {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 140px;
  height: 140px;
  pointer-events: none;
  z-index: 15;
}

.quantum-ring {
  position: absolute;
  border-radius: 50%;
  border: 1px solid;
  opacity: 0;
  animation: quantumSpin 4s ease-in-out infinite;
}

.quantum-ring-1 {
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border-color: var(--primary-glow);
  animation-delay: 0s;
}

.quantum-ring-2 {
  top: 15%;
  left: 15%;
  width: 70%;
  height: 70%;
  border-color: var(--secondary-glow);
  animation-delay: -1s;
  animation-direction: reverse;
}

.quantum-ring-3 {
  top: 30%;
  left: 30%;
  width: 40%;
  height: 40%;
  border-color: var(--accent-glow);
  animation-delay: -2s;
}

.quantum-center {
  position: absolute;
  top: 45%;
  left: 45%;
  width: 10%;
  height: 10%;
  border-radius: 50%;
  background: var(--primary-glow);
  opacity: 0;
  animation: quantumPulse 2s ease-in-out infinite;
}

.quantum-visualizer.active .quantum-ring {
  opacity: 0.8;
  animation-play-state: running;
}

.quantum-visualizer.active .quantum-center {
  opacity: 1;
  box-shadow: 0 0 20px var(--primary-glow);
}

@keyframes quantumSpin {
  0% {
    transform: rotate(0deg) scale(0.8);
    opacity: 0;
  }
  50% {
    transform: rotate(180deg) scale(1.1);
    opacity: 0.8;
  }
  100% {
    transform: rotate(360deg) scale(0.8);
    opacity: 0;
  }
}

@keyframes quantumPulse {
  0%, 100% {
    transform: scale(1);
    box-shadow: 0 0 10px var(--primary-glow);
  }
  50% {
    transform: scale(1.5);
    box-shadow: 0 0 30px var(--primary-glow);
  }
}

/* Energy field */
.energy-field {
  position: absolute;
  top: -20px;
  left: -20px;
  width: calc(100% + 40px);
  height: calc(100% + 40px);
  border-radius: 50%;
  background: radial-gradient(circle,
    transparent 60%,
    rgba(0, 245, 255, 0.1) 70%,
    rgba(255, 0, 128, 0.1) 80%,
    transparent 90%);
  opacity: 0;
  animation: energyPulse 3s ease-in-out infinite;
  z-index: 1;
}

.energy-field.active {
  opacity: 1;
}

@keyframes energyPulse {
  0%, 100% {
    transform: scale(0.9);
    opacity: 0;
  }
  50% {
    transform: scale(1.2);
    opacity: 0.6;
  }
}

/* Content section */
.transcript-content {
  flex: 1;
  overflow: hidden;
  z-index: 10;
}

.transcript-text-container {
  position: relative;
  background: linear-gradient(135deg,
    rgba(255, 255, 255, 0.08) 0%,
    rgba(255, 255, 255, 0.03) 100%);
  border-radius: 16px;
  padding: 20px;
  border: 1px solid rgba(255, 255, 255, 0.15);
  min-height: 100px;
  display: flex;
  flex-direction: column;
  backdrop-filter: blur(10px);
  box-shadow:
    inset 0 2px 0 rgba(255, 255, 255, 0.1),
    0 4px 20px rgba(0, 0, 0, 0.2);
}

.text-bg-effect {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg,
    transparent 0%,
    rgba(0, 245, 255, 0.05) 50%,
    transparent 100%);
  animation: textBgFlow 4s ease-in-out infinite;
  border-radius: 16px;
  pointer-events: none;
}

@keyframes textBgFlow {
  0%, 100% { transform: translateX(-100%); }
  50% { transform: translateX(100%); }
}

.scan-line {
  position: absolute;
  top: 0;
  left: 0;
  width: 2px;
  height: 100%;
  background: linear-gradient(to bottom,
    transparent 0%,
    var(--primary-glow) 50%,
    transparent 100%);
  opacity: 0;
  animation: scanMove 3s ease-in-out infinite;
  box-shadow: 0 0 10px var(--primary-glow);
}

.scan-line.active {
  opacity: 0.8;
}

@keyframes scanMove {
  0%, 100% {
    transform: translateX(0);
    opacity: 0;
  }
  10% { opacity: 0.8; }
  90% { opacity: 0.8; }
  100% {
    transform: translateX(calc(100vw - 2px));
    opacity: 0;
  }
}

.transcript-text {
  font-family: var(--font-family, 'Inter') !important;
  font-size: var(--font-size, 18px) !important;
  font-weight: var(--font-weight, 500) !important;
  line-height: var(--line-height, 1.6) !important;
  letter-spacing: var(--letter-spacing, 0.5px) !important;
  color: var(--text-color, white) !important;
  text-shadow: var(--text-shadow, '0 2px 8px rgba(0, 0, 0, 0.5)') !important;
  position: relative;
  z-index: 2;
  min-height: 1.6em;
  word-wrap: break-word;
  width: 100%;
  flex: 1;
  display: flex;
  align-items: center;
}

.cursor-modern {
  display: inline-block;
  font-weight: bold;
  margin-left: 2px;
  position: relative;
  top: -1px;
}

.cursor-core {
  position: relative;
  z-index: 2;
}

.cursor-glow {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: var(--primary-glow);
  filter: blur(4px);
  opacity: 0.6;
  z-index: 1;
}

.cursor-modern.blink {
  animation: modernCursorBlink 1.2s ease-in-out infinite;
}

@keyframes modernCursorBlink {
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.3; transform: scale(1.1); }
}

/* Progress indicator */
.typing-progress {
  position: absolute;
  bottom: 4px;
  left: 4px;
  right: 4px;
  height: 2px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 1px;
  overflow: hidden;
}

.progress-bar {
  height: 100%;
  background: linear-gradient(90deg,
    var(--primary-glow),
    var(--secondary-glow));
  border-radius: 1px;
  transition: width 0.1s ease-out;
  box-shadow: 0 0 10px var(--primary-glow);
  position: relative;
}

.progress-bar::after {
  content: '';
  position: absolute;
  top: 0;
  right: 0;
  width: 20px;
  height: 100%;
  background: linear-gradient(90deg,
    transparent,
    rgba(255, 255, 255, 0.6));
  animation: progressShine 2s ease-in-out infinite;
}

@keyframes progressShine {
  0%, 100% { opacity: 0; }
  50% { opacity: 1; }
}

/* Corner accents */
.corner-accent {
  position: absolute;
  width: 20px;
  height: 20px;
  border: 2px solid var(--primary-glow);
  opacity: 0.6;
  animation: cornerPulse 3s ease-in-out infinite;
}

.corner-top-left {
  top: 8px;
  left: 8px;
  border-right: none;
  border-bottom: none;
  border-top-left-radius: 8px;
}

.corner-top-right {
  top: 8px;
  right: 8px;
  border-left: none;
  border-bottom: none;
  border-top-right-radius: 8px;
  animation-delay: -0.5s;
}

.corner-bottom-left {
  bottom: 8px;
  left: 8px;
  border-right: none;
  border-top: none;
  border-bottom-left-radius: 8px;
  animation-delay: -1s;
}

.corner-bottom-right {
  bottom: 8px;
  right: 8px;
  border-left: none;
  border-top: none;
  border-bottom-right-radius: 8px;
  animation-delay: -1.5s;
}

@keyframes cornerPulse {
  0%, 100% {
    opacity: 0.4;
    box-shadow: none;
  }
  50% {
    opacity: 1;
    box-shadow: 0 0 15px var(--primary-glow);
  }
}

/* Mobile optimizations */
@media (max-width: 768px) {
  .live-transcript-overlay {
    bottom: 20px;
  }

  .live-transcript-container {
    max-width: 95%;
    padding: 16px;
    border-radius: 20px;
    gap: 16px;
  }

  .transcript-speaker-avatar {
    width: 80px;
    height: 80px;
    min-width: 80px;
  }

  .holo-frame-outer {
    top: -6px;
    left: -6px;
    width: calc(100% + 12px);
    height: calc(100% + 12px);
  }

  .holo-frame-inner {
    top: -3px;
    left: -3px;
    width: calc(100% + 6px);
    height: calc(100% + 6px);
  }

  .quantum-visualizer {
    width: 100px;
    height: 100px;
  }

  .avatar-fallback {
    font-size: 22px;
  }

  .speaker-badge-futuristic {
    padding: 6px 14px;
    font-size: 13px;
  }

  .status-ring {
    width: 14px;
    height: 14px;
  }

  .status-core {
    width: 5px;
    height: 5px;
  }

  .transcript-text-container {
    padding: 16px;
    min-height: 80px;
  }

  .corner-accent {
    width: 16px;
    height: 16px;
  }

  .particle {
    width: 1px;
    height: 1px;
  }
}

/* High contrast mode support */
@media (prefers-contrast: high) {
  .live-transcript-container {
    border: 2px solid var(--primary-glow);
    background: rgba(0, 0, 0, 0.9) !important;
  }

  .transcript-text {
    text-shadow: none !important;
    font-weight: 600 !important;
  }
}

/* Reduced motion support */
@media (prefers-reduced-motion: reduce) {
  * {
    animation-duration: 0.01ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: 0.01ms !important;
  }
}

/* Focus and accessibility */
.live-transcript-container:focus-within {
  outline: 2px solid var(--primary-glow);
  outline-offset: 4px;
}

/* Performance optimizations */
.live-transcript-container * {
  will-change: transform, opacity;
}

.particle, .quantum-ring, .energy-field {
  transform: translateZ(0);
  backface-visibility: hidden;
}
</style>
